import { useEffect, useMemo, useState } from 'react';
import { toUpperCase } from 'utils/toLowerCase';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';
import { PackageTabs } from '../PackageTabs';
import { OrderCancelDetailProps } from 'components/LightConversation/types';
import { OrderCancelDetailContent } from './OrderCancelDetailContent';
import { OrderCancelDetailContentSkeleton } from './OrderCancelDetailContentSkeleton';
import CancelApprovalBtns from 'views/Refund/components/CancelApprovalBtns';
import useTradeCancelInfo from 'views/Conversation/Sider/hooks/useTradeCancelInfo';
import { OrderDrawerDetailBody } from '../OrderDrawer/DetailBody';
import { Contact } from '../Contact';

interface OrderCancelButtonProps {
  order: Conversation.Trade.Vo;
  storeId: string;
  onUpdate: () => void;
}

const OrderCancelButton = (props: OrderCancelButtonProps) => {
  const { order, storeId, onUpdate } = props;
  const { data: cancelInfo } = useTradeCancelInfo({ trade: order, storeId });

  const { approvalBtnParams } = cancelInfo;

  return (
    <CancelApprovalBtns
      {...approvalBtnParams}
      onUpdate={onUpdate}
      buttonProps={{ type: 'primary', size: 'middle', className: '!mr-4' }}
    />
  );
};

export const OrderCancelDetail = (props: OrderCancelDetailProps) => {
  const { order, contactEnable = false } = props;
  const { externalTradeId, platform } = order;

  const {
    data: cancelOrderRelated,
    refetch,
    isLoading: isCancelOrderRelatedLoading,
  } = useQuery({
    ...queries.orders.cancelOrderRelated({
      orderId: externalTradeId,
      platform: toUpperCase(platform),
      pageSize: 10,
    }),
  });
  const cancelOrderData = cancelOrderRelated?.data;

  const [activeTab, setActiveTab] = useState(cancelOrderData?.[0]?.reverseOrderId || '');

  const options = useMemo(
    () =>
      cancelOrderData?.map((item) => ({
        value: item.reverseOrderId,
        label: item.reverseOrderId,
      })) || [],
    [cancelOrderData],
  );

  const activeOrder = useMemo(() => {
    return cancelOrderData?.find((item) => item.reverseOrderId === activeTab);
  }, [cancelOrderData, activeTab]);

  useEffect(() => {
    if (cancelOrderData && cancelOrderData[0]) {
      setActiveTab(cancelOrderData[0].reverseOrderId);
    }
  }, [cancelOrderData]);

  const showFooter = (activeOrder && activeOrder.operate?.length > 0) || contactEnable;

  return (
    <OrderDrawerDetailBody
      footer={
        showFooter ? (
          <>
            {activeOrder && activeOrder.operate?.length > 0 ? (
              <OrderCancelButton order={order} storeId={activeOrder.storeId} onUpdate={refetch} />
            ) : null}
            {contactEnable && activeOrder ? (
              <Contact
                platform={toUpperCase(platform)}
                orderSn={externalTradeId}
                region={toUpperCase(activeOrder.region) as General.UpperCaseRegion}
                externalBuyerId={activeOrder.buyerId}
                externalStoreId={activeOrder.externalStoreId}
                buyerNick={activeOrder.buyerName}
                returnSn={activeOrder.orderId}
                type="cancel"
                reverseOrderId={activeOrder.reverseOrderId}
                onClose={refetch}
              />
            ) : null}
          </>
        ) : null
      }
    >
      {isCancelOrderRelatedLoading ? (
        <OrderCancelDetailContentSkeleton />
      ) : (
        <>
          {options.length > 1 && (
            <PackageTabs
              value={activeTab}
              options={options}
              onChange={(value) => setActiveTab(value as string)}
              className="mb-4"
            />
          )}
          {activeOrder && <OrderCancelDetailContent {...props} cancelOrder={activeOrder} />}
        </>
      )}
    </OrderDrawerDetailBody>
  );
};
